import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Clock, ShieldX } from 'lucide-react';

const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth, logout } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    const verifyAuth = async () => {
      if (!user && !loading) {
        await checkAuth();
      }
      setIsChecking(false);
    };

    if (!loading) {
      verifyAuth();
    }
  }, [user, loading, checkAuth, location.state]);

  if (loading || isChecking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--c-bg)' }}
        data-testid="auth-loading"
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full animate-spin"
            style={{
              border: '2px solid var(--c-border)',
              borderTopColor: 'var(--c-gold)',
            }}
          />
          <span className="hud-eyebrow">Synchronisation</span>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirige vers la landing avec overlay auth automatique (?auth=1)
    // + ?next= pour retomber sur la route demandée après connexion.
    const next = encodeURIComponent(location.pathname + (location.search || ''));
    return <Navigate to={`/?auth=1&next=${next}`} replace />;
  }

  // Pending approval screen
  if (user.status === 'pending') {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--c-bg)' }}
        data-testid="pending-screen"
      >
        <div
          className="hud-card hud-card-glass hud-bracket max-w-md w-full text-center"
          style={{ padding: 32 }}
        >
          <span className="hud-bracket-bl" /><span className="hud-bracket-br" />
          <div
            className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
            style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.25)' }}
          >
            <Clock className="w-7 h-7" style={{ color: '#FDE68A' }} />
          </div>
          <span className="hud-eyebrow hud-eyebrow-gold">Compte en attente</span>
          <h1
            className="mt-2 mb-3 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', fontSize: 26, color: 'var(--c-text)' }}
          >
            Validation requise
          </h1>
          <p className="mb-6 text-[14px]" style={{ color: 'var(--c-text-2)' }}>
            Votre inscription est en cours de validation par un administrateur.
            Vous recevrez un accès dès que votre compte sera approuvé.
          </p>
          <div
            className="px-4 py-3 rounded-xl mb-6 text-[13px]"
            style={{
              background: 'rgba(234,179,8,0.08)',
              border: '1px solid rgba(234,179,8,0.22)',
              color: '#FDE68A',
            }}
          >
            Connecté en tant que <strong>{user.email}</strong>
          </div>
          <button
            onClick={async () => { await logout(); window.location.href = '/'; }}
            className="hud-btn hud-btn-secondary hud-btn-md w-full"
            data-testid="pending-logout-btn"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  // Banned screen
  if (user.status === 'banned') {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--c-bg)' }}
        data-testid="banned-screen"
      >
        <div
          className="hud-card hud-card-glass hud-bracket max-w-md w-full text-center"
          style={{ padding: 32 }}
        >
          <span className="hud-bracket-bl" /><span className="hud-bracket-br" />
          <div
            className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}
          >
            <ShieldX className="w-7 h-7" style={{ color: '#FCA5A5' }} />
          </div>
          <span className="hud-eyebrow" style={{ color: '#FCA5A5' }}>Compte suspendu</span>
          <h1
            className="mt-2 mb-3 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', fontSize: 26, color: 'var(--c-text)' }}
          >
            Accès révoqué
          </h1>
          <p className="mb-6 text-[14px]" style={{ color: 'var(--c-text-2)' }}>
            Votre compte a été suspendu. Cette décision est en cours d'examen.
            Contactez l'administrateur pour plus d'informations.
          </p>
          <button
            onClick={async () => { await logout(); window.location.href = '/'; }}
            className="hud-btn hud-btn-secondary hud-btn-md w-full"
            data-testid="banned-logout-btn"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
